///
/// EarthBrowser
///
/// Created by Matt Giger on December 24, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { VectorTileId } from './VectorTileId'

/**
 * Geometry types supported in vector tiles
 */
export enum VectorTileGeometryType {
	UNKNOWN = 0,
	POINT = 1,
	LINESTRING = 2,
	POLYGON = 3
}

/**
 * A single feature within a vector tile layer
 */
export interface VectorTileFeature {
	id: number | string | undefined
	type: VectorTileGeometryType
	properties: Record<string, unknown>
	/** Tile-local geometry: one array of {x, y} points per line/ring */
	geometry: { x: number, y: number }[][]
	extent: number
}

/**
 * A layer within a vector tile
 */
export interface VectorTileLayer {
	name: string
	version: number
	extent: number
	features: VectorTileFeature[]
	keys: string[]
	values: unknown[]
}

/**
 * Decoded vector tile data
 */
export interface DecodedVectorTile {
	tileId: VectorTileId
	layers: Map<string, VectorTileLayer>
	rawData: ArrayBuffer
}

/**
 * Options for fetching tiles
 */
export interface VectorTileFetchOptions {
	/** Source identifier (for caching) */
	source: string

	/** Base URL template (e.g., "https://example.com/{z}/{x}/{y}.pbf") */
	urlTemplate: string

	/** Cache TTL in milliseconds (default: 7 days) */
	cacheTTL?: number

	/** Force refresh from server */
	forceRefresh?: boolean

	/** Additional headers for the request */
	headers?: Record<string, string>

	/** Abort signal for cancellation */
	signal?: AbortSignal

	/** Maximum retries on failure */
	maxRetries?: number

	/** Retry delay in milliseconds */
	retryDelay?: number
}

/**
 * Options for batch fetching tiles
 */
export interface VectorTileBatchFetchOptions extends Omit<VectorTileFetchOptions, 'forceRefresh'> {
	/** Maximum concurrent requests */
	concurrency?: number

	/** Callback for progress updates */
	onProgress?: (loaded: number, total: number) => void

	/** Callback for individual tile completion */
	onTileLoaded?: (tileId: VectorTileId, success: boolean) => void
}

/**
 * Tile source configuration
 */
export interface VectorTileSource {
	id: string
	name: string
	urlTemplate: string
	minZoom: number
	maxZoom: number
	attribution?: string
	bounds?: [number, number, number, number]
	headers?: Record<string, string>
	cacheTTL?: number
}

/**
 * Index entry for a tile in a local MBTiles database
 */
export interface MBTilesIndexEntry {
	tileId: VectorTileId
	offset: number
	length: number
}

/**
 * MBTiles metadata
 */
export interface MBTilesMetadata {
	name?: string
	format?: string
	bounds?: string
	center?: string
	minzoom?: number
	maxzoom?: number
	attribution?: string
	description?: string
	type?: string
	version?: string
	json?: string
}

/**
 * Result of a tile fetch operation
 */
export interface TileFetchResult {
	tileId: VectorTileId
	success: boolean
	data?: ArrayBuffer
	error?: Error
	fromCache: boolean
	timestamp: number
}

/**
 * Statistics for the tile manager
 */
export interface VectorTileManagerStats {
	cachedTiles: number
	cacheSize: number
	averageTileSize: number
	sources: string[]
	oldestTile: number
	newestTile: number
	hitRate?: number
	missRate?: number
}

/**
 * Options for the vector tile manager
 */
export interface VectorTileManagerOptions {
	/** Maximum cache size in bytes (default: 100MB) */
	maxCacheSize?: number

	/** Default cache TTL in milliseconds (default: 7 days) */
	defaultCacheTTL?: number

	/** Enable automatic cache pruning */
	autoPrune?: boolean

	/** Prune interval in milliseconds (default: 5 minutes) */
	pruneInterval?: number

	/** Enable metrics tracking */
	enableMetrics?: boolean
}

/**
 * Tile request queue entry
 */
export interface TileRequest {
	tileId: VectorTileId
	options: VectorTileFetchOptions
	priority: number
	timestamp: number
	abortController: AbortController
	resolve: (result: TileFetchResult) => void
	reject: (error: Error) => void
}

/**
 * GeoJSON representation of a vector tile feature
 */
export interface VectorTileFeatureGeoJSON {
	type: 'Feature'
	id?: number | string
	properties: Record<string, unknown>
	geometry: {
		type: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon'
		coordinates: number[] | number[][] | number[][][]
	}
}

/**
 * Options for converting tiles to GeoJSON
 */
export interface ToGeoJSONOptions {
	/** Filter features by layer name */
	layers?: string[]

	/** Transform coordinates (useful for reprojection) */
	coordinateTransform?: (x: number, y: number) => [number, number]

	/** Filter features by property values */
	propertyFilter?: (properties: Record<string, unknown>) => boolean
}

